<script setup lang="ts">
import { dateTimeFormatter } from "@/utils/formater";

import type { InferFormValidatorSchema } from "maz-ui";
import { string, nonEmpty, pipe, email, maxLength } from "valibot";
import { useFormValidator } from "maz-ui";
import { postComment, replyComment } from "~/api/blog";
const { executeRecaptcha } = useGoogleRecaptcha();

const props = defineProps({
  comments: { required: true, type: Array },
  blog: { required: true, type: Object },
});

const emit = defineEmits(["getCommentById"]);
const gtm: any = useGtm();
const loading = ref(false);
const showSuccess = ref(false);
const replyData = ref<any>(null);

const countTotalComments = (commentList: any[]): number => {
  return commentList.reduce((total, comment) => {
    return total + 1 + countTotalComments(comment.replies || []);
  }, 0);
};

const totalComment = ref(countTotalComments(props.comments));

const commentsData = computed(() => {
  return props.comments.map((comment: any) => ({
    ...comment,
    avatar: `https://avatar.oxro.io/avatar.svg?name=${comment.name}`,
    replies: comment.replies?.map((reply: any) => ({
      ...reply,
      avatar: `https://avatar.oxro.io/avatar.svg?name=${reply.name}`,
    })),
  }));
});

// ------ Form
const defaultSchema = {
  name: pipe(string(), nonEmpty("Name is required")),
  email: pipe(
    string(),
    nonEmpty("Please enter your email."),
    email("Email not valid"),
    maxLength(150, "Your email is too long.")
  ),
  comment: pipe(string(), nonEmpty("Comment is required")),
};

const defaultFieldStates = {
  name: "",
  email: "",
  comment: "",
};

const schema = ref<any>(defaultSchema);
type Model = InferFormValidatorSchema<typeof schema>;

const { model, isSubmitting, handleSubmit, errorMessages, fieldsStates } =
  useFormValidator<Model>({
    schema,
    defaultValues: defaultFieldStates,
    options: { mode: "lazy", scrollToError: ".has-error" },
  });

const resetForm = () => {
  schema.value = {
    name: string(),
    email: string(),
    comment: string(),
  };
  model.value = { ...defaultFieldStates };
  setTimeout(() => {
    schema.value = { ...defaultSchema };
    emit("getCommentById");
    totalComment.value++;
  }, 100);
};

const scrollToForm = () => {
  const el: any = document.getElementById("form-comment");
  el.scrollIntoView({ behavior: "smooth" });
};

const scrollToComment = () => {
  const el: any = document.getElementById("section-comment");
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const setReplyComment = (commentId: string) => {
  replyData.value = commentId;
  scrollToForm();
};

const onSubmit = handleSubmit(async (formData) => {
  const { token } = await executeRecaptcha("submitcomment");

  if (token) {
    if (!props.blog?.id) {
      console.error("Blog ID is missing");
      return;
    }

    const submitData = {
      'g-recaptcha-response': token,
      ...formData,
      ...(replyData.value
        ? { reply_id: replyData.value }
        : { blog_id: props.blog.id }),
    };

    try {
      loading.value = true;

      const { status } = replyData.value
        ? await replyComment(submitData)
        : await postComment(submitData);

      if (status) {
        replyData.value = null;
        resetForm();
        scrollToComment();
      }

      gtm.trackEvent({
        event: "submit-data-comment",
        action: "click",
        value: 1,
      });
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      loading.value = false;
    }
  }
});
</script>

<template>
  <section id="section-comment">
    <div class="text-2xl font-light mb-4">{{ totalComment }} Comments:</div>

    <div v-for="comment in commentsData">
      <div :key="comment.id" class="mb-2">
        <div class="flex items-start gap-4">
          <MazAvatar :fallback-src="comment.avatar" size="13px" />
          <div class="w-full">
            <div class="text-slate-500 text-xs">
              {{ dateTimeFormatter(comment.created_at) }}
            </div>
            <div class="mb-1 flex justify-between items-center">
              <span>{{ comment.name }}</span>
              <span
                @click="setReplyComment(comment.id)"
                class="text-xs cursor-pointer"
                ><Icon name="mdi:reply-all" /> Reply</span
              >
            </div>
            <p class="text-slate-400 text-sm italic">"{{ comment.comment }}"</p>
          </div>
        </div>
        <div class="my-4 border-t border-gray-300"></div>
      </div>

      <div v-for="commentChild in comment.replies" class="ml-8 mb-2">
        <div :key="commentChild.id" class="flex items-start gap-4">
          <MazAvatar :fallback-src="commentChild.avatar" size="13px" />
          <div class="w-full">
            <div class="text-slate-500 text-xs">
              {{ dateTimeFormatter(commentChild.created_at) }}
            </div>
            <div class="mb-1 flex justify-between items-center">
              <span>{{ commentChild.name }}</span>
              <span
                class="text-xs cursor-pointer"
                @click="setReplyComment(commentChild.id)"
                ><Icon name="mdi:reply-all" /> Reply</span
              >
            </div>
            <p class="text-slate-400 text-sm italic">
              "{{ commentChild.comment }}"
            </p>
          </div>
        </div>
        <div class="my-4 border-t border-gray-300"></div>
      </div>
    </div>
  </section>

  <section id="form-comment" class="mt-4">
    <div class="text-2xl font-light mb-1">Leave a Replay:</div>
    <p>Your email address will not be publised. Required fields are marked*</p>
  </section>

  <!-- <MazDialog
    v-model="showSuccess"
    persistent
    class="text-center"
    id="dialog-ads-form"
  >
    <Icon name="icon-park-solid:success" size="45" class="text-primary mb-2" />
    <div class="text-2xl text-primary font-semibold mb-1">Congratulations,</div>
    <p>Your data was successfully submited!</p>
    <div class="flex justify-center mt-4">
      <MazBtn @click="closeSuccess()" color="primary"> Finish </MazBtn>
    </div>
  </MazDialog> -->

  <form @submit.prevent="onSubmit">
    <div v-if="!showSuccess" class="flex flex-col gap-4 mt-4 w-full">
      <span class="text-sm -mb-2">Comment*</span>
      <MazTextarea
        v-model="model.comment"
        autocomplete="off"
        :hint="errorMessages.comment"
        :error="!!errorMessages.comment"
        :success="fieldsStates.comment.valid"
        :class="{ 'has-error': !!errorMessages.comment }"
      />

      <div class="grid grid-cols-2 gap-4">
        <div>
          <div class="text-sm mb-2">Name*</div>
          <MazInput
            v-model="model.name"
            autocomplete="off"
            class="w-full"
            :hint="errorMessages.name"
            :error="!!errorMessages.name"
            :success="fieldsStates.name.valid"
            :class="{ 'has-error': !!errorMessages.name }"
          />
        </div>

        <div>
          <div class="text-sm mb-2">Email*</div>
          <MazInput
            v-model="model.email"
            id="input-email-data"
            autocomplete="off"
            class="w-full"
            :hint="errorMessages.email"
            :error="!!errorMessages.email"
            :success="fieldsStates.email.valid"
            :class="{ 'has-error': !!errorMessages.email }"
          />
        </div>
      </div>

      <MazBtn
        id="btn-comment-submit"
        :loading="isSubmitting"
        :disabled="loading"
        block
        type="submit"
      >
        <span>Submit</span>
      </MazBtn>

      <!-- <MazBtn
        block
        @click="schema.comment."
      >
        <span>Reset</span>
      </MazBtn> -->
    </div>
  </form>
</template>
